import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { marked } from 'marked';
import { api } from 'src/api';
import { exportCSV } from 'src/utils/exportCSV';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
import { USER_ROLES_BY_NAME, USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { currentRole } from './access';
const NO_OPTIONS = {
  cancelToken: {}
};

/** Answers built from these carry the cycle export behind them, so the raw rows are one click away. */
const RAW_DATA_TOOLS = ['get_allocation_audit', 'get_utility_allocation_export'];

/** Every report whose answer can be followed by the report email. */
const REPORT_TOOLS = [...RAW_DATA_TOOLS, 'get_reimbursement_kpis', 'get_reimbursement_report', 'get_reimbursement_receipt'];

/** Who may turn the monthly email on; the API refuses everyone else (PmHolderOrHigher). */
const MANAGES_EMAIL = [USER_ROLES_BY_NAME.PropertyManagerAccountHolder, USER_ROLES_BY_NAME.LivableAdmin, USER_ROLES_BY_NAME.LivableSuperAdmin];
const plain = text => text.replace(/\*\*|__|`/g, '').trim();

/** Every markdown table in an answer, as header and rows of plain cell text. */
export function tablesIn(markdown) {
  return marked.lexer(markdown).filter(t => t.type === 'table').map(t => ({
    header: t.header.map(c => plain(c.text)),
    rows: t.rows.map(r => r.map(c => plain(c.text)))
  }));
}
const saveBlob = (data, name) => {
  const url = URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
const ACTION = {
  textTransform: 'none',
  fontSize: 12.5,
  borderRadius: '8px',
  px: '10px',
  py: '3px'
};
// One read shared by every report in the chat, per signed-in user and company; dropped when the setting changes.
let settingsRead = null;
const settingsKey = () => {
  var _localStorage$getItem;
  return "".concat((_localStorage$getItem = localStorage.getItem(AUTH_TOKEN_KEYS.accessToken)) === null || _localStorage$getItem === void 0 ? void 0 : _localStorage$getItem.slice(-24), ":").concat(localStorage.getItem(USER_SETTINGS_KEYS.company));
};
export const resetMonthlySettings = () => {
  settingsRead = null;
};

/** The company's monthly report settings, read only by people the API lets change them. */
function useMonthlySettings() {
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    settings = _useState2[0],
    setSettings = _useState2[1];
  const allowed = MANAGES_EMAIL.includes(String(currentRole()));
  useEffect(() => {
    var _settingsRead, _settingsRead2;
    if (!allowed) return;
    const key = settingsKey();
    if (((_settingsRead = settingsRead) === null || _settingsRead === void 0 ? void 0 : _settingsRead.key) !== key) {
      settingsRead = {
        key,
        read: api.Allocations.getMonthlyReportSettings(NO_OPTIONS).then(resp => {
          var _response;
          return (_response = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _response !== void 0 ? _response : null;
        }).catch(() => {
          settingsRead = null;
          return null;
        })
      };
    }
    void ((_settingsRead2 = settingsRead) === null || _settingsRead2 === void 0 ? void 0 : _settingsRead2.read.then(setSettings));
  }, [allowed]);
  return allowed ? settings : null;
}
/** CSV of the tables in the answer, and the report's own raw export. Both run as the signed-in user. */
export function LivyReportActions(_ref) {
  var _useMonthlySettings;
  let body = _ref.body,
    _ref$tools = _ref.tools,
    tools = _ref$tools === void 0 ? [] : _ref$tools,
    exportArgs = _ref.exportArgs;
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    busy = _useState4[0],
    setBusy = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    menuAt = _useState6[0],
    setMenuAt = _useState6[1];
  const _useState7 = useState('idle'),
    _useState8 = _slicedToArray(_useState7, 2),
    mail = _useState8[0],
    setMail = _useState8[1];
  const sending = (_useMonthlySettings = useMonthlySettings()) === null || _useMonthlySettings === void 0 ? void 0 : _useMonthlySettings.sendingEnabled;
  const report = tools.some(t => REPORT_TOOLS.includes(t));
  const tables = tablesIn(body);
  const raw = tools.some(t => RAW_DATA_TOOLS.includes(t));
  if (!tables.length && !raw && !(report && sending)) return null;

  // The same range and properties the answer used, so the file and the chat agree to the cent.
  const downloadRaw = async format => {
    setMenuAt(null);
    setBusy(true);
    try {
      var _resp$headers$fileNa, _resp$headers;
      const resp = await api.Allocations.downloadUtilityAllocationExport(exportArgs ? _objectSpread(_objectSpread({}, exportArgs), {}, {
        format
      }) : {
        monthsBack: 12
      }, NO_OPTIONS);
      if (resp !== null && resp !== void 0 && resp.data) saveBlob(resp.data, (_resp$headers$fileNa = (_resp$headers = resp.headers) === null || _resp$headers === void 0 ? void 0 : _resp$headers['file-name']) !== null && _resp$headers$fileNa !== void 0 ? _resp$headers$fileNa : "utility-allocation-export.".concat(format));
    } finally {
      setBusy(false);
    }
  };

  // The existing monthly report email, to the signed-in user only, and only where sending is on.
  const emailMe = async () => {
    setMail('sending');
    try {
      await api.Allocations.sendMonthlyReport({
        audience: 'Me'
      }, NO_OPTIONS);
      setMail('sent');
    } catch (_unused) {
      setMail('error');
    }
  };
  return /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px",
    sx: {
      mt: '10px',
      flexWrap: 'wrap',
      rowGap: '6px'
    }
  }, tables.map((t, i) => {
    const name = "livy-table".concat(tables.length > 1 ? "-".concat(i + 1) : '');
    const label = tables.length > 1 ? "table ".concat(i + 1) : 'table';
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: i
    }, /*#__PURE__*/React.createElement(Button, {
      size: "small",
      variant: "outlined",
      sx: ACTION,
      onClick: () => exportCSV(t.header, t.rows, "".concat(name, ".csv"))
    }, "Download ".concat(label, " (CSV)")), /*#__PURE__*/React.createElement(Button, {
      size: "small",
      variant: "outlined",
      sx: ACTION,
      onClick: async () => {
        const resp = await api.Allocations.downloadTableXlsx({
          headers: t.header,
          rows: t.rows
        }).catch(() => null);
        if (resp !== null && resp !== void 0 && resp.data) saveBlob(resp.data, "".concat(name, ".xlsx"));
      }
    }, "Excel"));
  }), raw && exportArgs && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "outlined",
    sx: ACTION,
    disabled: busy,
    "aria-haspopup": "menu",
    "aria-expanded": Boolean(menuAt),
    onClick: e => setMenuAt(e.currentTarget)
  }, busy ? 'Preparing…' : 'Download data ▾'), /*#__PURE__*/React.createElement(Menu, {
    anchorEl: menuAt,
    open: Boolean(menuAt),
    onClose: () => setMenuAt(null)
  }, /*#__PURE__*/React.createElement(MenuItem, {
    onClick: () => downloadRaw('csv')
  }, "CSV"), /*#__PURE__*/React.createElement(MenuItem, {
    onClick: () => downloadRaw('xlsx')
  }, "Excel (.xlsx)"))), raw && !exportArgs && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "outlined",
    sx: ACTION,
    disabled: busy,
    onClick: () => downloadRaw('csv')
  }, busy ? 'Preparing…' : 'Download latest cycle (CSV)'), report && sending && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "outlined",
    sx: ACTION,
    disabled: mail === 'sending' || mail === 'sent',
    onClick: emailMe
  }, {
    idle: 'Email me the monthly report',
    sending: 'Sending…',
    sent: 'Sent to your email',
    error: 'Couldn’t send, try again'
  }[mail]));
}

/** "Utility allocation export, year to date, for 2 properties": the schedule in a person's words. */
export function describeSchedule(s) {
  var _s$propertyIds$length, _s$propertyIds;
  const report = {
    AllocationAudit: 'the utility allocation export',
    AccountsReceivable: 'accounts receivable',
    RecoveryKpis: 'the recovery report'
  }[s.reportKey];
  const range = {
    LastClosedCycle: 'for the month that just closed',
    YearToDate: 'year to date',
    LastMonths: "for the last ".concat(s.rangeMonths, " months")
  }[s.rangeRule];
  // The API sends null for "every property"; the offer frame sends [].
  const count = (_s$propertyIds$length = (_s$propertyIds = s.propertyIds) === null || _s$propertyIds === void 0 ? void 0 : _s$propertyIds.length) !== null && _s$propertyIds$length !== void 0 ? _s$propertyIds$length : 0;
  const scope = count === 0 ? '' : count === 1 ? ', for 1 property' : ", for ".concat(count, " properties");
  return "".concat(report, " ").concat(range).concat(scope);
}
const sameSchedule = (a, b) => {
  var _a$rangeMonths, _b$rangeMonths, _a$propertyIds, _b$propertyIds;
  return a.reportKey === b.reportKey && a.rangeRule === b.rangeRule && ((_a$rangeMonths = a.rangeMonths) !== null && _a$rangeMonths !== void 0 ? _a$rangeMonths : null) === ((_b$rangeMonths = b.rangeMonths) !== null && _b$rangeMonths !== void 0 ? _b$rangeMonths : null) &&
  // null means every property, the same as an empty list.
  [...((_a$propertyIds = a.propertyIds) !== null && _a$propertyIds !== void 0 ? _a$propertyIds : [])].sort().join() === [...((_b$propertyIds = b.propertyIds) !== null && _b$propertyIds !== void 0 ? _b$propertyIds : [])].sort().join();
};

/**
 * "Want me to run this for you every month?" after the same report request in two conversations. Yes schedules
 * exactly that request (report, range rule, properties), emailed to this person when each cycle closes.
 */
export function ScheduleOffer(_ref2) {
  var _list$items;
  let schedule = _ref2.schedule;
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    list = _useState0[0],
    setList = _useState0[1];
  const _useState1 = useState('idle'),
    _useState10 = _slicedToArray(_useState1, 2),
    state = _useState10[0],
    setState = _useState10[1];
  useEffect(() => {
    api.Allocations.getScheduledReports().then(resp => {
      var _response2;
      return setList((_response2 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _response2 !== void 0 ? _response2 : null);
    }).catch(() => setList(null));
  }, []);
  const exists = list === null || list === void 0 || (_list$items = list.items) === null || _list$items === void 0 ? void 0 : _list$items.some(i => i.enabled && sameSchedule(i, schedule));
  if (!(list !== null && list !== void 0 && list.sendingEnabled) || exists && state !== 'done') return null;
  const words = describeSchedule(schedule);
  const accept = async () => {
    setState('saving');
    try {
      await api.Allocations.createScheduledReport(schedule);
      setState('done');
    } catch (_unused2) {
      setState('error');
    }
  };
  return /*#__PURE__*/React.createElement(Box, {
    role: "group",
    "aria-label": "Monthly report",
    sx: {
      mt: '12px',
      p: '12px 14px',
      borderRadius: '10px',
      border: 1,
      borderColor: 'divider',
      bgcolor: 'action.hover'
    }
  }, state === 'done' ? /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13.5
    }
  }, "Done. I'll email you ", words, " when each billing cycle closes. Pause or stop it from the Reports tab here.") : /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "12px",
    alignItems: "center",
    sx: {
      flexWrap: 'wrap',
      rowGap: '8px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13.5,
      flex: 1,
      minWidth: 180
    }
  }, "Want me to run this for you every month? I'll email you ", words, " when each cycle closes."), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "contained",
    sx: ACTION,
    disabled: state === 'saving',
    onClick: accept
  }, "Email it monthly")), state === 'error' && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      mt: '8px',
      py: 0
    }
  }, "That didn't save. Try again in a moment."));
}
const EMAILS = {
  charge_export: {
    name: 'monthly charge export email',
    available: 'convergentExportSendingEnabled',
    field: 'convergentExportEmailEnabled'
  },
  recovery_report: {
    name: 'monthly recovery report email',
    available: 'sendingEnabled',
    field: 'emailEnabled'
  }
};

/**
 * The switch Livy offers when asked to turn a report email on or off. Read fresh and saved with the person's own
 * token, sending only this email's field, so the API decides who may change it and the other email is left alone.
 */
export function EmailOffer(_ref3) {
  let offer = _ref3.offer;
  const _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    settings = _useState12[0],
    setSettings = _useState12[1];
  const _useState13 = useState('idle'),
    _useState14 = _slicedToArray(_useState13, 2),
    state = _useState14[0],
    setState = _useState14[1];
  const allowed = MANAGES_EMAIL.includes(String(currentRole()));
  useEffect(() => {
    if (!allowed) return;
    api.Allocations.getMonthlyReportSettings(NO_OPTIONS).then(resp => {
      var _response3;
      return setSettings((_response3 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _response3 !== void 0 ? _response3 : null);
    }).catch(() => setSettings(null));
  }, [allowed]);
  const email = EMAILS[offer.email];
  const words = offer.on ? 'on' : 'off';
  if (!allowed) {
    return /*#__PURE__*/React.createElement(Typography, {
      sx: {
        mt: '12px',
        fontSize: 13.5
      }
    }, "Only an Account Holder can turn the ", email.name, " ", words, ".");
  }
  if (!(settings !== null && settings !== void 0 && settings[email.available])) return null;
  if (settings[email.field] === offer.on && state !== 'done') return null;
  const accept = async () => {
    setState('saving');
    try {
      await api.Allocations.saveMonthlyReportSettings({
        [email.field]: offer.on
      }, NO_OPTIONS);
      resetMonthlySettings();
      setState('done');
    } catch (_unused3) {
      setState('error');
    }
  };
  return /*#__PURE__*/React.createElement(Box, {
    role: "group",
    "aria-label": "Report email",
    sx: {
      mt: '12px',
      p: '12px 14px',
      borderRadius: '10px',
      border: 1,
      borderColor: 'divider',
      bgcolor: 'action.hover'
    }
  }, state === 'done' ? /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13.5
    }
  }, "Done. The ", email.name, " is ", words, " for your company.") : /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "12px",
    alignItems: "center",
    sx: {
      flexWrap: 'wrap',
      rowGap: '8px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13.5,
      flex: 1,
      minWidth: 180
    }
  }, "Turn the ", email.name, " ", words, " for your whole company?"), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "contained",
    sx: ACTION,
    disabled: state === 'saving',
    onClick: accept
  }, "Turn it ", words)), state === 'error' && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      mt: '8px',
      py: 0
    }
  }, "That didn't save. Try again in a moment."));
}